import { IconEdit, IconInfoCircle, IconLoader2, IconRefresh, IconX } from '@tabler/icons-react'
import { IntegrationAction, IntegrationDetails, IntegrationValue } from '../../types/IntegrationTypes'
import { useEffect, useState } from 'react'
import { Card, Switch, Tooltip } from '@radix-ui/themes'
import toast from 'react-hot-toast'
import { useAppDispatch } from '../../reducers/store'
import {
  deleteIntegration,
  executeUrlAction,
  fetchIntegrationsList,
  preAuthorizeIntegration,
  updateIntegrationsConfig,
  updateIverifyConfig,
} from '../../reducers/integrations'
import IntegrationRemoveModal from './IntegrationRemoveModal'
import { validateField } from '../../utils/validate'
import { useSelector } from 'react-redux'
import { selectUser } from '../../reducers/users'
import IntegrationInput from './IntegrationInput'
import { BOTTOM_TOAST_CONFIG } from '../toast/consts'

interface IIntegrationPopoutProps {
  popoutIntegration: IntegrationDetails | undefined
  setShowPopout: (showPopout: boolean) => void
  available: boolean
}

function IntegrationPopout(props: IIntegrationPopoutProps) {
  const { popoutIntegration, setShowPopout, available } = props
  const di = useAppDispatch()
  const { darkModeEnabled } = useSelector(selectUser)
  const [isDirty, setIsDirty] = useState(false)
  const [updatedIntegrationValues, setUpdatedIntegrationValues] = useState<IntegrationDetails | undefined>(undefined)
  const [integrationEnabled, setIntegrationEnabled] = useState(true)
  const [iverifyOptionsEnabled, setIverifyOptionsEnabled] = useState<{ [key: string]: boolean }>({})
  const [showDisconnectModal, setShowDisconnectModal] = useState(false)
  const [executingAction, setExecutingAction] = useState<IntegrationAction | null>(null)
  const [optionsReadOnly, setOptionsReadOnly] = useState(false)
  const [settingsReadOnly, setSettingsReadOnly] = useState(false)
  const [selectedRadio, setSelectedRadio] = useState<string | undefined>(undefined)
  const [isAvailable, setIsAvailable] = useState(available)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    isAvailable ? setIntegrationEnabled(false) : setIntegrationEnabled(true)
    popoutIntegration?.iverifyOptions.forEach((option) => {
      option.value !== null
        ? setIverifyOptionsEnabled((prevState) => ({ ...prevState, [option.serverValue]: true }))
        : setIverifyOptionsEnabled((prevState) => ({ ...prevState, [option.serverValue]: false }))
    })

    setUpdatedIntegrationValues(popoutIntegration)
  }, [])

  useEffect(() => {
    setIsAvailable(available)
  }, [available])

  useEffect(() => {
    setUpdatedIntegrationValues(popoutIntegration)
  }, [popoutIntegration])

  useEffect(() => {
    setOptionsReadOnly(integrationEnabled)
    setSettingsReadOnly(integrationEnabled)
  }, [integrationEnabled])

  const handleInputChange = () => {
    setIsDirty(true)
  }

  const handleRadioChange = (value: string) => {
    setSelectedRadio(value)
  }

  const authorize = async (formData: IntegrationValue[]) => {
    if (
      updatedIntegrationValues?.authorization?.type === 'url' &&
      updatedIntegrationValues?.authorization?.urlType === 'iverify' &&
      updatedIntegrationValues?.authorization?.url
    ) {
      const payload = {
        integrationValues: JSON.stringify(formData),
        integrationServerValue: updatedIntegrationValues!.serverValue,
      }
      const redirect_uri = await di(preAuthorizeIntegration(updatedIntegrationValues?.authorization?.url, payload))
      if (redirect_uri) {
        return redirect_uri
      }
    } else if (
      updatedIntegrationValues?.authorization?.type === 'url' &&
      updatedIntegrationValues?.authorization?.urlType === 'external' &&
      updatedIntegrationValues?.authorization?.url
    ) {
      const baseUrl = window.location.origin
      const url = updatedIntegrationValues?.authorization?.url.replace(/\{\{url_token\}\}/g, baseUrl)
      window.open(url, '_self')
    }
  }

  const executeAction = async (action: IntegrationAction) => {
    setExecutingAction(action)
    try {
      if (action.type === 'refresh_url' && action.urlType === 'iverify' && action.url) {
        const response = await di(executeUrlAction(action?.url))
        if (response && response.error === undefined && response.message) {
          toast.success(response.message, BOTTOM_TOAST_CONFIG)
        } else {
          toast.error(response.error, BOTTOM_TOAST_CONFIG)
        }
      }
    } finally {
      setExecutingAction(null)
    }
  }

  const saveIntegrationSettings = async (formData: IntegrationValue[]) => {
    if (updatedIntegrationValues?.serverValue === 'intune_mtd') {
      formData.push(
        { serverValue: 'active', value: 'true' },
        { serverValue: 'state', value: 'OPEN' },
        { serverValue: 'failure_count', value: '0' }
      )
    }

    const payload = {
      integrationValues: JSON.stringify(formData),
      integrationServerValue: updatedIntegrationValues!.serverValue,
    }

    const [successful, response] = await di(updateIntegrationsConfig(payload))

    if (successful) {
      toast.success('Integration settings saved successfully', BOTTOM_TOAST_CONFIG)
      di(fetchIntegrationsList())
      setIntegrationEnabled(true)
      setIsDirty(false)
      setIsAvailable(false)
    } else {
      if (response !== undefined) {
        toast.error(response, BOTTOM_TOAST_CONFIG)
      } else {
        toast.error('Failed to save integration settings', BOTTOM_TOAST_CONFIG)
      }
    }
    setIsSubmitting(false)
  }

  const saveIverifySettings = async (formData: IntegrationValue[]) => {
    const payload = {
      integrationValues: JSON.stringify(formData),
      integrationServerValue: updatedIntegrationValues!.serverValue,
    }
    const successful = await di(updateIverifyConfig(payload))
    if (successful) {
      toast.success('Integration options saved successfully', BOTTOM_TOAST_CONFIG)
      di(fetchIntegrationsList())
      setIntegrationEnabled(true)
      setIsDirty(false)
    } else {
      toast.error('Failed to save integration options', BOTTOM_TOAST_CONFIG)
    }
  }

  const removeIntegration = async () => {
    const deleted = await di(deleteIntegration({ integrationServerValue: updatedIntegrationValues!.serverValue }))
    if (deleted) {
      toast.success('Integration disconnected successfully', BOTTOM_TOAST_CONFIG)
      di(fetchIntegrationsList())
      setShowPopout(false)
    } else {
      toast.error('Failed to delete integration', BOTTOM_TOAST_CONFIG)
    }
  }

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', updatedIntegrationValues?.authorization ? () => {} : handleBeforeUnload)

    return () => {
      window.removeEventListener(
        'beforeunload',
        updatedIntegrationValues?.authorization ? () => {} : handleBeforeUnload
      )
    }
  }, [isDirty, updatedIntegrationValues])

  const getNotifications = () => {
    if (popoutIntegration?.serverValue === 'google_scim' && !settingsReadOnly) {
      return (
        <Card size="2" className="p-0 m-0 bg-neutral-4 dark:bg-cardFill border-transparent" variant="ghost">
          <div className="flex flex-col gap-2 p-3 text-sm">
            <div className="flex gap-2 font-medium items-center">
              <IconInfoCircle size={20} />
              <p>
                {selectedRadio == 'group_users'
                  ? 'Set Up A Google Group For iVerify'
                  : 'Adding All Google Users To iVerify'}
              </p>
            </div>
            <p className="text-neutral-11 dark:text-neutral-9">
              {selectedRadio == 'group_users'
                ? 'Only one user group can be linked through this integration, so we recommend creating a dedicated group for seamless setup'
                : 'If you wish to not notify users that they have been added to iVerify, you can disable the email in your notification settings'}
            </p>
          </div>
        </Card>
      )
    } else if (popoutIntegration?.category === 'SCIM' && !settingsReadOnly) {
      return (
        <Card size="2" className="p-0 m-0 bg-neutral-4 dark:bg-cardFill border-transparent" variant="ghost">
          <div className="flex flex-col gap-2 p-3 text-sm">
            <div className="flex gap-2 font-medium items-center">
              <IconInfoCircle size={20} />
              <p>Generate Authorization Header</p>
            </div>
            <p className="text-neutral-11 dark:text-neutral-9">
              Click Generate to create your authentication header. This ensures your SCIM setup is correctly configured
              before the header is issued. The header will appear above when generation is complete.
            </p>
          </div>
        </Card>
      )
    } else if (popoutIntegration?.serverValue === 'intune_mtd') {
      return (
        <>
          <div className="justify-center text-black text-base font-semibold">Overview</div>
          <div className="self-stretch justify-center text-black text-sm font-normal mt-1">
            Connect Microsoft Intune Mobile Threat Defense (MTD) to send device threat levels from iVerify back to
            Intune. Once connected, assign the integration to a policy to ensure non-compliant devices are reported
            correctly to Microsoft Intune.
          </div>
          <Card size="2" className="p-0 m-0 bg-neutral-4 dark:bg-cardFill border-transparent my-4" variant="ghost">
            <div className="flex flex-col gap-2 p-3 text-sm">
              <div className="flex gap-2 font-medium items-center">
                <IconInfoCircle size={20} />
              </div>
              <p className="text-neutral-11 dark:text-neutral-9">
                Only devices added through Intune MDM or SCIM will be blocked by Microsoft MTD.
              </p>
            </div>
          </Card>
          {!settingsReadOnly && (
            <>
              <div className="justify-center text-black text-base font-semibold">Next step</div>
              <div className="self-stretch justify-center text-black text-sm font-normal mt-1">
                Continue to Microsoft Intune to grant permissions to your iVerify organization
              </div>
            </>
          )}
        </>
      )
    } else {
      return null
    }
  }

  const googleTermsOfService = () => {
    if (popoutIntegration?.serverValue === 'google_scim') {
      return (
        <Card size="2" className="p-0 m-0 bg-neutral-4 dark:bg-cardFill border-transparent" variant="ghost">
          <div className="flex flex-col gap-2 p-3 text-sm">
            <div className="flex gap-2 font-medium items-center">
              <IconInfoCircle size={20} />
              <p>Limited Use of User Data</p>
            </div>
            <p className="text-neutral-11 dark:text-neutral-9">
              The use of raw or derived user data received from Workspace APIs will adhere to the Google User Data
              Policy, including the Limited Use requirements.
            </p>
            <p className="text-neutral-11 dark:text-neutral-9">
              For more information, please refer to&nbsp;
              <a
                href="https://developers.google.com/workspace/workspace-api-user-data-developer-policy#limited-use"
                target="_blank"
                rel="noopener noreferrer"
                className="text-mainPurple underline"
              >
                Workspace API User Data and Developer Policy
              </a>
            </p>
          </div>
        </Card>
      )
    }
  }

  return (
    <>
      <div
        className="bg-black bg-opacity-50 fixed top-0 left-0 w-full h-full z-50"
        onClick={() => {
          if (isDirty) {
            if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
              setShowPopout(false)
            }
          } else {
            setShowPopout(false)
          }
        }}
      >
        <div
          className={`top-0 my-[7px] mr-[11px] rounded-lg h-[calc(100vh_-_14px)] fixed flex flex-col w-[500px] sm:w-[600px] gap-6 right-0 bg-neutral-2 dark:bg-mainBackground z-50 border-mainBorder border-t border-l shadow-2xl p-4`}
          onClick={(e) => {
            e.stopPropagation()
          }}
        >
          <div className="w-full flex justify-between">
            <p className="text-mainText text-lg font-medium mt-1">{updatedIntegrationValues?.name}</p>
            <div className="flex items-center justify-center gap-2">
              {/* Integration Actions */}
              {integrationEnabled &&
                updatedIntegrationValues?.actions &&
                updatedIntegrationValues.actions.length > 0 && (
                  <>
                    {updatedIntegrationValues.actions.map((action) => (
                      <Tooltip key={action.label} content={action.label}>
                        <div
                          className={`h-8 w-fit cursor-pointer bg-white dark:bg-transparent border-mainBorder border rounded flex items-center justify-center px-2 ${executingAction === action ? 'opacity-50 pointer-events-none' : ''}`}
                          onClick={() => executeAction(action)}
                        >
                          {action.icon == 'refresh' ? <IconRefresh size={22} /> : action.label}
                        </div>
                      </Tooltip>
                    ))}
                  </>
                )}
              <div
                className="h-8 w-8 cursor-pointer border bg-white dark:bg-transparent rounded flex items-center justify-center"
                onClick={() => {
                  if (isDirty) {
                    if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
                      setShowPopout(false)
                    }
                  } else {
                    setShowPopout(false)
                  }
                }}
              >
                <IconX size={22} />
              </div>
            </div>
          </div>
          <div
            className={`overflow-y-auto flex flex-col ${updatedIntegrationValues?.configurationOptions && updatedIntegrationValues?.configurationOptions.length > 0 ? 'gap-6' : ''}`}
          >
            {/* Integration Settings */}
            <div
              className={`${updatedIntegrationValues?.configurationOptions && updatedIntegrationValues?.configurationOptions.length > 0 ? 'border-mainBorder border rounded-lg' : ''}`}
            >
              <form
                id="integrations-settings-form"
                onSubmit={async (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (isSubmitting) return
                  setIsSubmitting(true)
                  const formData = new FormData(e.target as HTMLFormElement)
                  const data = Array.from(formData.entries()).map(([key, value]) => ({
                    serverValue: key,
                    value: value.toString().trim(),
                    label: updatedIntegrationValues?.configurationOptions.find((option) => option.serverValue === key)
                      ?.label,
                    format: updatedIntegrationValues?.configurationOptions.find((option) => option.serverValue === key)
                      ?.format,
                    required: updatedIntegrationValues?.configurationOptions.find(
                      (option) => option.serverValue === key
                    )?.required,
                    minLength: updatedIntegrationValues?.configurationOptions.find(
                      (option) => option.serverValue === key
                    )?.minLength,
                    maxLength: updatedIntegrationValues?.configurationOptions.find(
                      (option) => option.serverValue === key
                    )?.maxLength,
                  }))

                  const errors: string[] = []
                  data.forEach((field) => {
                    const error = validateField(field)
                    if (error) {
                      errors.push(error)
                    }
                  })

                  if (errors.length > 0) {
                    toast.error(errors.join('\n'), BOTTOM_TOAST_CONFIG)
                    setIsSubmitting(false)
                    return
                  }

                  if (updatedIntegrationValues?.authorization) {
                    const redirect_uri = await authorize(data)
                    if (typeof redirect_uri === 'string' && redirect_uri) {
                      window.open(redirect_uri, '_self')
                    }
                  } else {
                    saveIntegrationSettings(data)
                  }
                }}
              >
                {updatedIntegrationValues?.configurationOptions &&
                  updatedIntegrationValues?.configurationOptions.length > 0 && (
                    <div className="w-full flex justify-between bg-neutral-2 dark:bg-contentBackground rounded-t-lg border-mainBorder border-b items-center px-4 py-3">
                      <p className="text-mainText font-medium">Configuration</p>
                      {integrationEnabled &&
                        updatedIntegrationValues?.configurationOptions?.some((val) => val.readOnly !== true) && (
                          <div className="flex items-center gap-2">
                            {!settingsReadOnly ? (
                              <div className="flex items-center gap-2">
                                <button
                                  className="h-8 px-3 cursor-pointer border-mainBorder bg-white dark:bg-transparent border rounded flex items-center justify-center text-sm font-medium"
                                  type="button"
                                  disabled={isSubmitting}
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    const form = document.getElementById(
                                      'integrations-settings-form'
                                    ) as HTMLFormElement
                                    if (form) {
                                      form.reset()
                                    }
                                    setUpdatedIntegrationValues(
                                      popoutIntegration ? JSON.parse(JSON.stringify(popoutIntegration)) : undefined
                                    )
                                    setSettingsReadOnly(true)
                                    setIsDirty(false)
                                  }}
                                >
                                  Cancel
                                </button>
                                <button
                                  className="h-8 px-3 cursor-pointer border-mainBorder text-white bg-mainPurple hover:bg-tertiaryPurple active:active:bg-tertiaryPurple border rounded flex items-center justify-center text-sm font-medium"
                                  type="submit"
                                >
                                  Save Changes
                                </button>
                              </div>
                            ) : (
                              <button
                                className="h-8 px-1 cursor-pointer border-mainBorder bg-white dark:bg-transparent border rounded flex items-center justify-center"
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  setSettingsReadOnly(false)
                                  setIsDirty(true)
                                }}
                              >
                                <IconEdit size={22} />
                              </button>
                            )}
                          </div>
                        )}
                    </div>
                  )}

                <div
                  className={`flex flex-col bg-neutral-1 dark:bg-contentBackground rounded-b-lg ${updatedIntegrationValues?.configurationOptions && updatedIntegrationValues?.configurationOptions.length > 0 ? 'gap-4 p-4' : ''}`}
                >
                  {updatedIntegrationValues?.configurationOptions.map((option) => (
                    <>
                      {!option.hidden ? (
                        <div className="flex flex-col gap-2" key={option.serverValue}>
                          <p className="w-[160px] font-medium truncate text-mainText text-sm">{option.label}</p>
                          <IntegrationInput
                            canEdit={!settingsReadOnly}
                            option={option}
                            available={isAvailable}
                            handleInputChange={handleInputChange}
                            handleRadioChange={handleRadioChange}
                            darkModeEnabled={darkModeEnabled}
                          />
                        </div>
                      ) : (
                        // ✅ Still include a hidden input so its value is submitted
                        <input type="hidden" name={option.serverValue} value={option.value ?? ''} readOnly />
                      )}
                    </>
                  ))}
                </div>
              </form>
            </div>

            {getNotifications()}
            {googleTermsOfService()}

            {/* Integration Options */}

            {integrationEnabled &&
              updatedIntegrationValues?.iverifyOptions &&
              updatedIntegrationValues.iverifyOptions.length > 0 && (
                <>
                  {/* Integration Options */}
                  <div className="border-mainBorder border rounded-lg">
                    <form
                      id="integrations-options-form"
                      onSubmit={(e) => {
                        e.preventDefault()
                        const formData = new FormData(e.target as HTMLFormElement)
                        const data = Array.from(formData.entries()).map(([key, value]) => ({
                          serverValue: key,
                          value: value.toString().trim(),
                        }))

                        let error = false
                        data.forEach(({ value }) => {
                          if (!value) {
                            error = true
                          }
                        })

                        if (error) {
                          toast.error('Please fill out all fields', BOTTOM_TOAST_CONFIG)
                          return
                        }
                        saveIverifySettings(data)
                      }}
                    >
                      <div className="w-full flex justify-between bg-neutral-2 dark:bg-contentBackground rounded-t-lg border-mainBorder border-b items-center px-4 py-3">
                        <p className="text-mainText font-medium">Options</p>
                        {integrationEnabled && (
                          <>
                            {!optionsReadOnly ? (
                              <div className="flex items-center gap-2">
                                <button
                                  className="h-8 px-3 cursor-pointer border-mainBorder bg-white dark:bg-transparent border rounded flex items-center justify-center text-sm font-medium"
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    const form = document.getElementById('integrations-options-form') as HTMLFormElement
                                    if (form) {
                                      form.reset()
                                    }
                                    if (updatedIntegrationValues?.iverifyOptions) {
                                      const initialOptions: { [key: string]: boolean } = {}
                                      updatedIntegrationValues.iverifyOptions.forEach((option) => {
                                        initialOptions[option.serverValue] = option.value !== null
                                      })
                                      setIverifyOptionsEnabled(initialOptions)
                                    }
                                    setOptionsReadOnly(true)
                                    setUpdatedIntegrationValues(
                                      popoutIntegration ? JSON.parse(JSON.stringify(popoutIntegration)) : undefined
                                    )
                                    setIsDirty(false)
                                  }}
                                >
                                  Cancel
                                </button>
                                <button
                                  className="h-8 px-3 cursor-pointer border-mainBorder text-white bg-mainPurple hover:bg-tertiaryPurple active:active:bg-tertiaryPurple border rounded flex items-center justify-center text-sm font-medium"
                                  type="submit"
                                >
                                  Save Changes
                                </button>
                              </div>
                            ) : (
                              <button
                                className="h-8 px-1 cursor-pointer border-mainBorder bg-white dark:bg-transparent border rounded flex items-center justify-center"
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  setOptionsReadOnly(false)
                                  setIsDirty(true)
                                }}
                              >
                                <IconEdit size={22} />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                      <div className="p-4 bg-neutral-1 dark:bg-mainBackground rounded-b-lg">
                        {updatedIntegrationValues?.iverifyOptions.map((option) => (
                          <div key={option.serverValue}>
                            <div className="flex justify-between pb-2">
                              <p className="w-[160px] truncate text-mainText text-sm font-medium">{option.label}</p>
                              {optionsReadOnly ? (
                                <div className="text-sm font-medium">
                                  {iverifyOptionsEnabled[option.serverValue] ? 'On' : 'Off'}{' '}
                                </div>
                              ) : (
                                <Switch
                                  size="2"
                                  className={`${darkModeEnabled ? 'dark' : 'light'}`}
                                  checked={iverifyOptionsEnabled[option.serverValue] ?? false}
                                  onCheckedChange={(e) => {
                                    setIverifyOptionsEnabled({
                                      ...iverifyOptionsEnabled,
                                      [option.serverValue]: e,
                                    })
                                    setIsDirty(true)
                                  }}
                                />
                              )}
                            </div>
                            {iverifyOptionsEnabled[option.serverValue] && !option.hidden && (
                              <IntegrationInput
                                canEdit={!optionsReadOnly}
                                option={option}
                                available={isAvailable}
                                handleInputChange={handleInputChange}
                                darkModeEnabled={darkModeEnabled}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </form>
                  </div>
                </>
              )}
          </div>
          <div className="flex flex-col grow items-end justify-end">
            <hr className="w-full mb-4 border-1 border-neutral-4" />
            {integrationEnabled ? (
              <>
                {/* Disconnect Integration */}
                <div
                  className="h-8 w-full cursor-pointer border border-Red-9 text-Red-9 px-3 rounded flex items-center justify-center"
                  onClick={() => setShowDisconnectModal(true)}
                >
                  Disconnect Integration
                </div>
              </>
            ) : (
              <button
                className={`h-8 w-full border border-transparent text-white bg-mainPurple px-3 rounded flex items-center justify-center ${isSubmitting ? 'disabled opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-tertiaryPurple active:bg-tertiaryPurple'}`}
                type="submit"
                form="integrations-settings-form"
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <IconLoader2 className="animate-spinSlow" size={20} />{' '}
                    <span>
                      {updatedIntegrationValues?.authorization?.provider
                        ? 'Connecting'
                        : updatedIntegrationValues?.category === 'SCIM'
                          ? 'Generating'
                          : 'Saving'}
                    </span>
                  </div>
                ) : updatedIntegrationValues?.authorization?.provider ? (
                  `Continue to ${updatedIntegrationValues?.authorization?.provider}`
                ) : updatedIntegrationValues?.category === 'SCIM' ? (
                  'Generate'
                ) : (
                  'Save'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      {showDisconnectModal && (
        <IntegrationRemoveModal
          name={updatedIntegrationValues!.name}
          setShowModal={setShowDisconnectModal}
          handleRemoveIntegration={removeIntegration}
          category={updatedIntegrationValues!.category}
        />
      )}
    </>
  )
}

export default IntegrationPopout
